import React from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { empMeta } from '../../pointsWatching/employeeColors';
import {
  NAVY,
  INDIGO,
  LAVENDER,
  PAGE_BG,
  STATUS,
  money,
  signed,
} from './matchTicketsHelpers';

/**
 * Employee Tickets page (Match Tickets spec §6). Rendered as a full page reached
 * from a machine row (the toolbar back arrow returns to the list), not an overlay.
 * Every ticket for one machine in the exact order it was paid, with the employee
 * who wrote it and per-employee totals. Read-only.
 */
class EmployeeTicketsTable extends React.Component {
  groupByEmployee = (tickets) => {
    const map = {};
    tickets.forEach((t) => {
      const name = t.employee || 'Unknown';
      if (!map[name]) map[name] = { name, total: 0, count: 0 };
      map[name].total += Number(t.amount) || 0;
      map[name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  };

  daySubtotal = (tickets, day) =>
    tickets
      .filter((t) => t.day === day)
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);

  render() {
    const { row, session, onOpenPdf } = this.props;
    if (!row) return null;

    const tickets = row.tickets || [];
    const st = STATUS[row.status] || STATUS.even;
    const byEmployee = this.groupByEmployee(tickets);
    const sessionNumber = session?.sessionNumber ?? '';
    const sessionDay = session?.sessionDay ?? '';

    return (
      <div style={{ background: PAGE_BG, padding: 16, minHeight: '100%' }}>
        {/* Machine header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div
            style={{
              flexShrink: 0,
              width: 48,
              height: 48,
              borderRadius: 12,
              background: LAVENDER,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1, color: '#8f95c9' }}>MCH</span>
            <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: INDIGO }}>{row.machine}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: NAVY }}>Machine {row.machine}</h2>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#9aa1c0' }}>
              Employee Tickets · Session {sessionNumber} · {sessionDay}
            </p>
          </div>
        </div>

        {/* Result banner */}
        <div style={{ borderRadius: 16, padding: 12, background: st.bg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {row.status === 'even' ? (
              <CheckCircleIcon style={{ fontSize: 18, color: st.color }} />
            ) : (
              <WarningIcon style={{ fontSize: 18, color: st.color }} />
            )}
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: st.color }}>
              {st.label}
              {Number(row.diff) !== 0 && ` · ${money(row.diff)}`}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
              marginTop: 12,
            }}
          >
            {[
              { label: 'Reading Out', value: money(row.readingOut) },
              { label: 'Ticket Out', value: money(row.ticketOut) },
              {
                label: 'Difference',
                value: Number(row.diff) === 0 ? '$0.00' : signed(row.diff),
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  borderRadius: 12,
                  background: 'rgba(255,255,255,.75)',
                  padding: 8,
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: st.color,
                    opacity: 0.7,
                  }}
                >
                  {s.label}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 800, color: st.color }}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Employee chips */}
        {byEmployee.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: '#9aa1c0',
              }}
            >
              Who wrote these tickets
            </p>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {byEmployee.map((e) => {
                const c = empMeta(e.name, e.name);
                return (
                  <div
                    key={e.name}
                    style={{
                      flexShrink: 0,
                      borderRadius: 12,
                      padding: '8px 12px',
                      background: c.fill,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: c.ink }}>{e.name}</p>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: c.ink }}>
                      {money(e.total)}
                    </p>
                    <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: c.ink, opacity: 0.75 }}>
                      {e.count} {e.count === 1 ? 'ticket' : 'tickets'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ticket list */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 4px',
              marginBottom: 8,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: '#9aa1c0',
              }}
            >
              Tickets in order paid
            </p>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                fontWeight: 700,
                color: '#9aa1c0',
              }}
            >
              <AccessTimeIcon style={{ fontSize: 11 }} /> first → last
            </span>
          </div>

          {tickets.length === 0 ? (
            <div
              style={{
                borderRadius: 16,
                padding: 32,
                textAlign: 'center',
                border: '1.5px dashed #dfe2f0',
              }}
            >
              <ConfirmationNumberIcon style={{ fontSize: 22, color: '#c9cde0' }} />
              <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 700, color: NAVY }}>
                No tickets on this machine
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9aa1c0' }}>
                The machine read a payout with no ticket written down.
              </p>
            </div>
          ) : (
            <div style={{ borderRadius: 16, overflow: 'hidden', border: '1.5px solid #eef0f6', background: '#fff' }}>
              {tickets.map((t, i) => {
                const c = empMeta(t.employee, t.employee);
                const newDay = i === 0 || tickets[i - 1].day !== t.day;
                return (
                  <div key={i}>
                    {newDay && (
                      <div
                        style={{
                          padding: '6px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: LAVENDER,
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: 0.8,
                            textTransform: 'uppercase',
                            color: INDIGO,
                          }}
                        >
                          {t.day}
                        </p>
                        <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: INDIGO }}>
                          {money(this.daySubtotal(tickets, t.day))}
                        </p>
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        borderTop: newDay ? 'none' : '1px solid #f2f3f8',
                      }}
                    >
                      <span
                        style={{
                          width: 20,
                          textAlign: 'center',
                          fontSize: 10,
                          fontWeight: 800,
                          color: '#c1c6dc',
                        }}
                      >
                        {t.seq != null ? t.seq : i + 1}
                      </span>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 800,
                          background: c.fill,
                          color: c.ink,
                        }}
                      >
                        {(t.employee || '').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: NAVY }}>
                          {money(t.amount)}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 500, color: '#9aa1c0' }}>
                          {t.employee} · Machine {row.machine}
                        </p>
                      </div>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#6b7194' }}>{t.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Total + PDF action */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 16,
            padding: '12px 16px',
            background: LAVENDER,
            marginTop: 16,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: INDIGO }}>
            Tickets ({tickets.length})
          </p>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: INDIGO }}>{money(row.ticketOut)}</p>
        </div>

        <button
          onClick={() => onOpenPdf(row.machine)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: 12,
            borderRadius: 12,
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            background: INDIGO,
            marginTop: 12,
          }}
        >
          <DescriptionIcon style={{ fontSize: 16 }} /> Open PDF — Machine {row.machine}
        </button>
      </div>
    );
  }
}

export default EmployeeTicketsTable;
